import { useEffect, useMemo, useState } from 'react'
import { useAuthContext } from '@/lib/auth-context'
import { useBearer, Section, useToast } from './_admin-utils'

interface AnalyticsSummary {
  totals?: {
    users: number;
    teachers: number;
    sponsors: number;
    admins: number;
  };
  recent?: Array<{
    id: string;
    role: string;
    created_at: string;
  }>;
}

interface DailyMetrics {
  signups?: Array<{
    day: string;
    total?: number;
    teachers?: number;
    sponsors?: number;
    admins?: number;
  }>;
  actions?: Array<{
    day: string;
    actions: number;
  }>;
}

interface RandomizedItem {
  id: string;
  name: string;
  weight: number;
}

interface SparkPoint {
  x: string;
  y: number;
}

interface CardProps {
  label: string;
  value: number;
}

interface SparkCardProps {
  title: string;
  data: SparkPoint[];
  foot: string;
  loading: boolean;
}

interface SparklineProps {
  data?: SparkPoint[];
}

const SAMPLE_PROFILES = [
  'Avery Johnson',
  'Jordan Ellis',
  'Sasha Martinez',
  'Devon Carter',
  'Bailey Nguyen',
  'Imani Brooks',
  'Noah Patel',
  'Lena Ortiz',
  'Micah Thompson',
  'Grace Chen',
  'Aria Singh',
  'Hunter Lee',
]

const randomInt = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min
const generateRandomized = () => {
  const shuffled = [...SAMPLE_PROFILES]
    .map((name) => ({ name, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .slice(0, 5)
  return shuffled.map(({ name }, index) => ({
    id: `${name}-${Date.now()}-${index}`,
    name,
    weight: randomInt(1, 100),
  }))
}

export default function Analytics() {
  const { profile } = useAuthContext()
  const bearer = useBearer()
  const { show, ToastHost } = useToast()
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null)
  const [daily, setDaily] = useState<DailyMetrics | null>(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [loadingDaily, setLoadingDaily] = useState(false)
  const [randomized, setRandomized] = useState<RandomizedItem[]>(() => generateRandomized())

  const isAdmin = ['admin', 'superadmin'].includes(profile?.role ?? '')
  const totals = summary?.totals || { users: 0, teachers: 0, sponsors: 0, admins: 0 }
  const recentSignups = Array.isArray(summary?.recent) ? summary.recent : []

  useEffect(() => {
    if (!bearer || !isAdmin) return
    let cancelled = false
    ;(async () => {
      try {
        setLoadingSummary(true)
        const res = await fetch('/api/v1/admin/analytics/summary', { headers: { ...bearer } })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json?.error?.message || json?.error || 'Failed to load analytics summary')
        if (!cancelled) setSummary(json?.data || json)
      } catch (error: unknown) {
        if (!cancelled) {
          setSummary(null)
          show((error as Error | null)?.message || 'Unable to load analytics summary')
        }
      } finally {
        if (!cancelled) setLoadingSummary(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bearer, isAdmin, show])

  useEffect(() => {
    if (!bearer || !isAdmin) return
    let cancelled = false
    ;(async () => {
      try {
        setLoadingDaily(true)
        const res = await fetch('/api/v1/admin/analytics/daily?days=14', { headers: { ...bearer } })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json?.error || 'Failed to load daily metrics')
        if (!cancelled) setDaily(json)
      } catch (error: unknown) {
        if (!cancelled) {
          setDaily(null)
          show((error as Error | null)?.message || 'Unable to load daily metrics')
        }
      } finally {
        if (!cancelled) setLoadingDaily(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bearer, isAdmin, show])

  const sparkSignups = useMemo(
    () => (daily?.signups || []).map((d) => ({ x: d.day, y: d.total ?? 0 })),
    [daily?.signups],
  )
  const sparkActions = useMemo(
    () => (daily?.actions || []).map((d) => ({ x: d.day, y: d.actions })),
    [daily?.actions],
  )

  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-cocoa/70">You do not have permission to view this page.</div>
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-6 min-w-0">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Analytics</h1>
        <p className="text-sm text-cocoa/70 max-w-2xl">
          Monitor community growth, review recent role changes, and simulate randomized selections. Data is refreshed on
          demand via secure admin endpoints.
        </p>
      </header>

      <Section
        title="Analytics Overview"
        actions={<a href="/portal/admin" className="text-sm underline">← Admin</a>}
      >
        {loadingSummary && !summary ? <div className="text-sm">Loading totals…</div> : null}
        {summary && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <Card label="Total Users" value={totals.users} />
            <Card label="Teachers" value={totals.teachers} />
            <Card label="Sponsors" value={totals.sponsors} />
            <Card label="Admins" value={totals.admins} />
          </div>
        )}

        {daily && (
          <div className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <SparkCard
                title="Signups (Total, 14d)"
                data={sparkSignups}
                foot={`${sparkSignups.length} days`}
                loading={loadingDaily}
              />
              <SparkCard
                title="Teachers (14d)"
                data={(daily.signups || []).map((d) => ({ x: d.day, y: d.teachers ?? 0 }))}
                foot="role"
                loading={loadingDaily}
              />
              <SparkCard
                title="Sponsors (14d)"
                data={(daily.signups || []).map((d) => ({ x: d.day, y: d.sponsors ?? 0 }))}
                foot="role"
                loading={loadingDaily}
              />
              <SparkCard
                title="Admins (14d)"
                data={(daily.signups || []).map((d) => ({ x: d.day, y: d.admins ?? 0 }))}
                foot="role"
                loading={loadingDaily}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <SparkCard
                title="Actions (14d)"
                data={sparkActions}
                foot={`${sparkActions.length} days`}
                loading={loadingDaily}
              />
            </div>
          </div>
        )}

        {recentSignups.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-cocoa mb-2">Recent signups (7d)</h3>
            <ul className="space-y-1 text-xs text-cocoa/70">
              {recentSignups.map((item) => (
                <li key={item.id}>
                  <span className="font-medium text-cocoa">{formatRole(item.role)}</span> •{' '}
                  <span>{new Date(item.created_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <Section
        title="Test Randomized"
        actions={
          <button
            type="button"
            onClick={() => setRandomized(generateRandomized())}
            className="rounded-full bg-terracotta px-4 py-2 text-sm font-semibold text-white shadow"
          >
            Run Demo
          </button>
        }
      >
        <p className="text-sm text-cocoa/70 mb-3">
          Generate five mock nominees with randomized weights to preview how the assignment engine consumes weighted pools.
          This operates entirely client-side for demonstration only.
        </p>
        <div className="overflow-hidden rounded-xl border border-white/70">
          <table className="min-w-full divide-y divide-white/60 text-sm">
            <thead className="bg-shell/60 text-cocoa/70 uppercase tracking-wide text-xs">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Weight</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/60 bg-white/60">
              {randomized.map((row) => (
                <tr key={row.id} className="hover:bg-shell/40 transition-colors">
                  <td className="px-4 py-3 text-cocoa/90">{row.name}</td>
                  <td className="px-4 py-3 text-cocoa/80 font-mono">{row.weight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <ToastHost />
    </div>
  )
}

function Card({ label, value }: CardProps) {
  return (
    <div className="p-3 border rounded bg-white">
      <div className="text-xs text-cocoa/70">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  )
}

function SparkCard({ title, data, foot, loading }: SparkCardProps) {
  const hasData = data && data.length > 0
  return (
    <div className="p-3 border rounded bg-white">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold text-sm">{title}</h3>
        <div className="text-xs text-cocoa/70">{loading && !hasData ? 'Loading…' : foot}</div>
      </div>
      {hasData ? <Sparkline data={data} /> : <div className="text-xs text-cocoa/60">No data</div>}
    </div>
  )
}

function Sparkline({ data = [] }: SparklineProps) {
  const w = 320
  const h = 80
  const pad = 6
  const ys = data.map((d) => Number(d.y || 0))
  const max = Math.max(1, ...ys)
  const stepX = data.length > 1 ? (w - pad * 2) / (data.length - 1) : 0
  const pts = data
    .map((d, i) => {
      const x = pad + i * stepX
      const y = h - pad - (Number(d.y || 0) / max) * (h - pad * 2)
      return `${x},${y}`
    })
    .join(' ')
  const last = ys[ys.length - 1] || 0
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="sparkline">
      {pts ? <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /> : null}
      <text x={w - pad} y={h - 4} fontSize="10" textAnchor="end" fill="currentColor">
        {last}
      </text>
    </svg>
  )
}

const ROLE_LABELS: Record<string, string> = {
  sponsor: 'Sponsor',
  teacher: 'Teacher',
  admin: 'Admin',
  superadmin: 'Super Admin',
}

const formatRole = (role: string): string => ROLE_LABELS[role] || 'User'
